/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Channel, Measure } from 'cio/utils/metrics/common';
import { ActionMetricRow } from 'cio/components/campaign-metrics-page/message-metrics-card';
import { GREEN_500, YELLOW_500, GRAY_400 } from 'cio/utils/colors';
import ActionModel, { ActionType } from 'cio/models/action';
import actionInfo from 'cio/utils/action-info';
import CampaignModel from 'cio/models/campaign';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { ClickSource } from 'cio/utils/analytics/transforms/campaign';

interface CampaignMetricsPageMessageMetricsCardRowArgs {
  campaign: CampaignModel;
  channels: Channel[];
  row: ActionMetricRow;
  selectedMeasures: Measure[];
  firstCellClass?: string;
}

export default class CampaignMetricsPageMessageMetricsCardRow extends Component<CampaignMetricsPageMessageMetricsCardRowArgs> {
  @service analytics!: AnalyticsService;

  get splitStartDate() {
    if (!this.args.row.isSplit) {
      return null;
    }

    let [, variationB] = this.args.row.action.splitActions;

    // We use variation B's create date as the split create date, because
    // creating a split actually just modifies the underlying action, which has
    // been created earlier. The create date is also copied to variation A, so
    // we can't use that either.
    return variationB.action.created;
  }

  get parentAction(): ActionModel | null {
    if (!this.args.row.isVariation) {
      return null;
    }

    return this.args.row.action.parentAction;
  }

  get isActive() {
    if (this.args.row.isVariation) {
      return (this.parentAction?.get('isSplitRunning') ?? false) || this.args.row.badge.active;
    }

    if (this.args.row.isSplit) {
      return this.args.row.action.isSplitRunning;
    }

    return false;
  }

  get isPaused() {
    if (this.args.row.isVariation) {
      return this.parentAction?.get('isSplitPaused');
    }

    if (this.args.row.isSplit) {
      return this.args.row.action.isSplitPaused;
    }

    return false;
  }

  get isInactive() {
    if (this.args.row.isVariation) {
      return this.parentAction?.get('isSplitInactive');
    }

    if (this.args.row.isSplit) {
      return this.args.row.action.isSplitInactive;
    }

    return false;
  }

  get testLabelClass() {
    if (this.isActive) {
      return 'is-success';
    }

    if (this.isPaused) {
      return 'is-warning';
    }

    if (this.isInactive) {
      return 'is-pending';
    }

    return null;
  }

  get testDotColor() {
    if (this.isActive) {
      return GREEN_500;
    }

    if (this.isPaused) {
      return YELLOW_500;
    }

    if (this.isInactive) {
      return GRAY_400;
    }

    return null;
  }

  get totalActionInfo() {
    if (!this.args.row.isTotal || this.args.channels.length !== 1) {
      return null;
    }

    return actionInfo(`${this.args.channels[0]}_action` as ActionType);
  }

  @action
  trackNavigate() {
    this.analytics.track('campaign', 'workflow_existing_item_clicked', {
      model: this.args.campaign,
      action: this.args.row.action,
      trayTab: 'build',
      source: ClickSource.MetricsMessagesTable,
    });
  }
}
